import { BootstrapIcon, getBootstrapIconUrl } from '@/components/webcomponents/Global/icons'
import {
  tryLoadDependencies,
  tryLoadLightDOMStyles,
  WebAwesomeComponent,
} from '@/components/webcomponents/Global/loadDependency'
import { GLOBAL_VERSION_CONTEXT } from '@/components/webcomponents/Global/versioncontext'
import { getNavEnterpriseStyles } from './NavEnterprise.styles'
import { getCdnUrl, getEnvironment } from '@/config/runtime'
import type { } from './types/NavEnterprise.type'
import './NavEnterpriseMenu/NavEnterpriseMenu'
import type { NavEnterpriseMenu } from './NavEnterpriseMenu/NavEnterpriseMenu'
import type {
  NavEnterpriseMenuApiResponse,
  NavEnterpriseMenuItemApiResponse,
} from './NavEnterpriseMenu/types/NavEnterpriseMenu.type'

const iconLeft = getBootstrapIconUrl(BootstrapIcon.ChevronLeft)
const iconRight = getBootstrapIconUrl(BootstrapIcon.ChevronRight)
const iconClose = getBootstrapIconUrl(BootstrapIcon.X)

const t = document.createElement('template')
t.innerHTML = `
  <nav class="nav-enterprise-container">
    <div class="logo-container">
      <slot name="logo-expanded" class="logo-expanded">
        <img class="logo-expanded-default" alt="TruStage" />
      </slot>
      <slot name="logo-collapsed" class="logo-collapsed">
        <img class="logo-collapsed-default" alt="TruStage" />
      </slot>
      <slot name="logo" class="logo-single"></slot>
      <slot name="logo-actions" class="logo-actions">
        <wa-button class="mobile-close" appearance="outlined" variant="neutral" size="small" aria-label="Close navigation">
          <wa-icon src="${iconClose}" aria-hidden="true" label="Close navigation"></wa-icon>
        </wa-button>
      </slot>
    </div>
    <div class="nav-top">
      <slot></slot>
    </div>
    <div class="nav-bottom">
      <slot name="footer"></slot>
      <wa-button
        size="small"
        appearance="outlined"
        class="collapse-toggle"
        aria-label="Click to Close"
        aria-expanded="true"
      >
        <wa-icon slot="start" src="${iconLeft}"></wa-icon> <span></span>
      </wa-button>
    </div>
  </nav>
`
const TemplateNavEnterprise = t

class NavEnterprise extends HTMLElement {
  private isCollapsed = false
  private toggleHandler: (() => void) | null = null
  private mobileOpen = false
  private returnFocus: HTMLElement | null = null
  private menuAbortController: AbortController | null = null
  private navEnterpriseEndpointOverride: string = ''

  constructor() {
    super()

    tryLoadDependencies(document, [WebAwesomeComponent.Button, WebAwesomeComponent.Icon])

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getNavEnterpriseStyles().content.cloneNode(true))
    shadow.appendChild(TemplateNavEnterprise.content.cloneNode(true))

    const imagePath = getEnvironment().environment === 'development' ? 'assets/images' : 'images'
    for (const [selector, filename] of [
      ['.logo-expanded-default', 'Trustage-Logo-SB.svg'],
      ['.logo-collapsed-default', 'TruStage-Logo-Icon.svg'],
    ]) {
      shadow.querySelector(selector)?.setAttribute('src', getCdnUrl(imagePath, filename))
    }

    tryLoadLightDOMStyles('NavEnterprise.css')
  }

  connectedCallback() {
    document.AscendAwesome?.LogManager?.logDebug('NavEnterprise connectedCallback')

    const toggleButton = this.shadowRoot?.querySelector('.collapse-toggle')
    if (toggleButton) {
      this.toggleHandler = () => this.toggleCollapse()
      toggleButton.addEventListener('click', this.toggleHandler)
    }

    this.addEventListener('enterprise-nav-close', this.onCloseBtnClick)
    this.shadowRoot?.querySelector('.mobile-close')?.addEventListener('click', this.closeMobileMenu)
    this.shadowRoot?.addEventListener('slotchange', this.updateLogoSlots)
    this.updateLogoSlots()

    const collapsedAttr = this.getAttribute('collapsed')
    if (collapsedAttr !== null) {
      this.setCollapsed(collapsedAttr === 'true')
    } else {
      const persistedCollapsed = localStorage.getItem('nav-enterprise-collapsed')
      this.setCollapsed(persistedCollapsed === 'true')
    }

    this.updateMobileMenuVisibility()

    //we need to grab the default slot to see if the ts-nav-enterprise-menu is provided by the user
    //this is useful so we know whether to automatically load the default menu or not
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])')
    const children = slot?.assignedNodes({ flatten: true }) ?? []
    const hasOwnMenu = children.some((node) => node.nodeName === 'TS-NAV-ENTERPRISE-MENU')

    //there is no manual menu supplanting (should be default behavior)
    //then load it for them
    if (!hasOwnMenu) {
      let url = GLOBAL_VERSION_CONTEXT.NAV_ENTERPRISE_ENDPOINT

      //this attribute allows easy override without implementing menu-item or :items creation
      if (this.navEnterpriseEndpointOverride) {
        url = this.navEnterpriseEndpointOverride
      }

      //we are lost
      if (!url) {
        console.error('NAV_ENTERPRISE_ENDPOINT is not defined')
        return
      }

      const menu = document.createElement('ts-nav-enterprise-menu') as NavEnterpriseMenu
      this.appendChild(menu)
      menu.loading = true
      menu.skeletonCount = 4

      this.menuAbortController?.abort()
      const controller = new AbortController()
      this.menuAbortController = controller
      void this.loadMenu(url, menu, controller.signal)
    }
  }

  private async loadMenu(url: string, menu: NavEnterpriseMenu, signal: AbortSignal) {
    try {
      const response = await fetch(url, { signal })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const data = (await response.json()) as NavEnterpriseMenuApiResponse
      if (signal.aborted) return

      const links = data.links
      const currentUrl = window.location.href.toLowerCase()

      // Longest root URL first so the most specific item wins
      const bySpecificity = [...links].sort(
        (a, b) => (b.destinationRootUrl?.length ?? 0) - (a.destinationRootUrl?.length ?? 0),
      )

      let active: NavEnterpriseMenuItemApiResponse | null = null
      for (const link of bySpecificity) {
        if (
          link.destinationRootUrl &&
          currentUrl.startsWith(link.destinationRootUrl.toLowerCase())
        ) {
          active = link
          break
        }
      }

      //this maps the api response back onto menu-item required objects.
      menu.items = links.map((link) => ({
        label: link.linkLabel,
        href: link.linkUrl,
        iconName: link.iconName,
        iconLibrary: link.iconLibrary,
        iconVariant: link.iconVariant,
        iconSrc: link.iconUrl,
        active: link === active,
        target: link.linkTarget,
      }))
      menu.loading = false
    } catch (err: unknown) {
      if (signal.aborted) return
      document.AscendAwesome?.LogManager?.logInfo(`NavEnterprise menu request failed: ${err}`)
    }
  }

  disconnectedCallback() {
    const toggleButton = this.shadowRoot?.querySelector('.collapse-toggle')
    if (toggleButton && this.toggleHandler) {
      toggleButton.removeEventListener('click', this.toggleHandler)
      this.toggleHandler = null
    }
    this.removeEventListener('enterprise-nav-close', this.onCloseBtnClick)
    this.shadowRoot
      ?.querySelector('.mobile-close')
      ?.removeEventListener('click', this.closeMobileMenu)
    document.removeEventListener('keydown', this.onMobileKeyDown)
    this.shadowRoot?.removeEventListener('slotchange', this.updateLogoSlots)
    this.mobileOpen = false
    this.returnFocus = null
    this.menuAbortController?.abort()
    this.menuAbortController = null
  }

  private updateLogoSlots = () => {
    const expanded = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="logo-expanded"]')
    const single = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="logo"]')
    const hasExpanded = Boolean(expanded?.assignedElements().length)
    const hasSingle = Boolean(single?.assignedElements().length)
    if (expanded) expanded.hidden = !hasExpanded && hasSingle
    if (single) single.hidden = hasExpanded || !hasSingle
  }

  private onCloseBtnClick = (event: Event) => {
    if ((event as CustomEvent).type === 'enterprise-nav-close') {
      this.closeMobileMenu()
    }
  }

  private closeMobileMenu = () => {
    this.removeAttribute('enterprise-nav-open')
  }

  private onMobileKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && window.innerWidth <= 576 && this.mobileOpen) {
      event.preventDefault()
      this.closeMobileMenu()
    }
  }

  toggleCollapse() {
    this.setCollapsed(!this.isCollapsed)
  }

  setCollapsed(collapsed: boolean) {
    this.isCollapsed = collapsed
    const navContainer = this.shadowRoot?.querySelector('.nav-enterprise-container')
    const toggleButton = this.shadowRoot?.querySelector('.collapse-toggle')
    const icon = toggleButton?.querySelector('wa-icon')
    const span = toggleButton?.querySelector('span')
    const label = collapsed ? 'Expand Sidebar' : 'Click to Close'

    if (navContainer) {
      navContainer.classList.toggle('collapsed', collapsed)
    }

    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', String(!collapsed))
      toggleButton.setAttribute('aria-label', label)
    }

    if (icon) {
      icon.setAttribute('src', collapsed ? iconRight : iconLeft)
    }

    if (span) {
      span.textContent = label
    }

    this.classList.toggle('collapsed', collapsed)
    localStorage.setItem('nav-enterprise-collapsed', String(collapsed))

    this.dispatchEvent(
      new CustomEvent('nav-collapse-change', {
        detail: { collapsed },
        bubbles: true,
        composed: true,
      }),
    )
  }

  updateMobileMenuVisibility() {
    const shadow = this.shadowRoot
    if (!shadow) return

    const container = shadow.querySelector('.nav-enterprise-container') as HTMLElement | null
    if (!container) return

    const isOpen = this.hasAttribute('enterprise-nav-open')
    container.classList.toggle('enterprise-nav-open', isOpen)
    if (isOpen === this.mobileOpen) return
    this.mobileOpen = isOpen

    if (isOpen) {
      document.addEventListener('keydown', this.onMobileKeyDown)
      if (window.innerWidth <= 576) {
        let active = document.activeElement
        while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement
        this.returnFocus = active instanceof HTMLElement && active !== document.body ? active : null
        queueMicrotask(() => {
          if (!this.isConnected || !this.mobileOpen || window.innerWidth > 576) return
          const actions = this.shadowRoot?.querySelector<HTMLSlotElement>(
            'slot[name="logo-actions"]',
          )
          const assigned = actions?.assignedElements()[0]
          const close = assigned?.matches('button, wa-button')
            ? assigned
            : (assigned?.querySelector('button, wa-button') ??
              this.shadowRoot?.querySelector('.mobile-close'))
            ; (close as HTMLElement | null)?.focus()
        })
      }
    } else {
      document.removeEventListener('keydown', this.onMobileKeyDown)
      if (this.returnFocus?.isConnected && window.innerWidth <= 576) this.returnFocus.focus()
      this.returnFocus = null
    }
    this.dispatchEvent(
      new CustomEvent('nav-open-change', {
        detail: { open: isOpen },
        bubbles: true,
        composed: true,
      }),
    )
  }

  static get observedAttributes() {
    return ['collapsed', 'enterprise-nav-open', 'nav-enterprise-endpoint-override']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'collapsed') {
      this.setCollapsed(newValue === 'true')
    } else if (name === 'enterprise-nav-open') {
      this.updateMobileMenuVisibility()
    } else if (name === 'nav-enterprise-endpoint-override') {
      this.navEnterpriseEndpointOverride = newValue ?? ''
    }
  }
}

if (!customElements.get('ts-nav-enterprise')) {
  customElements.define('ts-nav-enterprise', NavEnterprise)
}
export { NavEnterprise }
